import React from 'react';
import { ArrowRight, Phone, Mail, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const scrollToSection = (e, href) => {
    e.preventDefault();
    document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
  };

  const quickLinks = [
    { label: 'À propos', href: '#about' },
    { label: 'Services', href: '#services' },
    { label: 'Tarifs', href: '#pricing' },
    { label: 'Témoignages', href: '#testimonials' },
    { label: 'Contact', href: '#contact' }
  ];

  return (
    <footer className="bg-[#050505] text-white relative" data-testid="footer">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C9A55C]/50 to-transparent" />
      
      {/* CTA Section */}
      <div className="border-b border-[#C9A55C]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20">
          <div className="flex flex-col md:flex-row items-center justify-between gap-10">
            <div className="text-center md:text-left">
              <h2 className="text-3xl md:text-4xl font-serif font-bold mb-3">
                Prêt à <span className="text-[#C9A55C]">simplifier</span> vos démarches ?
              </h2>
              <p className="text-white/50">Premier échange gratuit et sans engagement.</p>
            </div>
            <Button
              onClick={(e) => scrollToSection(e, '#contact')}
              className="btn-gold rounded-none h-16 px-12 text-sm font-semibold tracking-wider uppercase"
              data-testid="footer-cta"
            >
              Contactez-nous
              <ArrowRight className="ml-3 w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand */}
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_service-devis-1/artifacts/32x86mnu_IMG_4011.jpeg"
                alt="HELP ADN"
                className="h-12 w-auto"
              />
              <span className="text-2xl font-bold font-serif tracking-wider">
                <span className="text-white">HELP</span>
                <span className="text-[#C9A55C]">ADN</span>
              </span>
            </div>
            <p className="text-white/50 text-sm leading-relaxed">
              Solutions IA & administratives pour entreprises, particuliers et associations.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold text-[#C9A55C] tracking-wider uppercase text-sm mb-6">Navigation</h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <a href={link.href} onClick={(e) => scrollToSection(e, link.href)} className="text-sm text-white/50 hover:text-[#C9A55C] transition-colors">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Informations */}
          <div>
            <h3 className="font-semibold text-[#C9A55C] tracking-wider uppercase text-sm mb-6">Informations</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/guide-renovation" className="text-sm text-white/50 hover:text-[#C9A55C] transition-colors">
                  Aides à la rénovation
                </Link>
              </li>
              <li>
                <Link to="/guide-aides" className="text-sm text-white/50 hover:text-[#C9A55C] transition-colors">
                  Aides officielles
                </Link>
              </li>
              <li>
                <Link to="/partenaires" className="text-sm text-white/50 hover:text-[#C9A55C] transition-colors">
                  Partenaires
                </Link>
              </li>
              <li>
                <Link to="/mentions-legales" className="text-sm text-white/50 hover:text-[#C9A55C] transition-colors">
                  Mentions légales
                </Link>
              </li>
              <li>
                <Link to="/politique-confidentialite" className="text-sm text-white/50 hover:text-[#C9A55C] transition-colors">
                  Politique de confidentialité
                </Link>
              </li>
              <li>
                <Link to="/cgv" className="text-sm text-white/50 hover:text-[#C9A55C] transition-colors">
                  CGV
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-semibold text-[#C9A55C] tracking-wider uppercase text-sm mb-6">Contact</h3>
            <ul className="space-y-4">
              <li>
                <a href="tel:0684632855" className="flex items-center gap-3 text-sm text-white/50 hover:text-[#C9A55C] transition-colors">
                  <Phone className="w-4 h-4" />06 84 63 28 55
                </a>
              </li>
              <li>
                <a href="mailto:contact@helpadn.fr" className="flex items-center gap-3 text-sm text-white/50 hover:text-[#C9A55C] transition-colors">
                  <Mail className="w-4 h-4" />contact@helpadn.fr
                </a>
              </li>
              <li className="flex items-center gap-3 text-sm text-white/50">
                <MapPin className="w-4 h-4" />Hauts-de-France
              </li>
            </ul>
          </div>

          {/* Hours */}
          <div>
            <h3 className="font-semibold text-[#C9A55C] tracking-wider uppercase text-sm mb-6">Horaires</h3>
            <div className="space-y-3 text-sm text-white/50">
              <div className="flex justify-between"><span>Lundi - Vendredi</span><span>9h - 18h</span></div>
              <div className="flex justify-between"><span>Samedi</span><span>Sur RDV</span></div>
              <div className="flex justify-between"><span>Dimanche</span><span>Fermé</span></div>
              <p className="pt-4 border-t border-[#C9A55C]/10 text-[#C9A55C]">Réponse rapide dans les 2h</p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-[#C9A55C]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-white/30">
            <p>© {currentYear} HELP'ADN – Jennifer Vanderbergue. Tous droits réservés. | SIRET : 102 160 124 00016</p>
            <div className="flex items-center gap-8">
              <Link to="/mentions-legales" className="hover:text-[#C9A55C] transition-colors">Mentions légales</Link>
              <Link to="/politique-confidentialite" className="hover:text-[#C9A55C] transition-colors">Confidentialité</Link>
              <Link to="/cgv" className="hover:text-[#C9A55C] transition-colors">CGV</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
